import { InstagramAuditResult } from '../../types/socialMediaAudit';

// Global definition for jspdf loaded via CDN in index.html
declare const jspdf: any;

export const generateSocialMediaAuditPDF = async (
  auditResult: InstagramAuditResult,
  companyName: string,
  logoBase64?: string,
  customNotes?: string
): Promise<any> => {
  const { jsPDF } = jspdf;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;

  const brandColor = [79, 70, 229]; // Indigo-600
  const darkSlate = [15, 23, 42]; // Slate-900
  const mutedText = [100, 116, 139]; // Slate-500

  const addPageFooter = (pageNum: number) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
    doc.text(
      `Prepared by ${companyName || 'BrandGenius'} • Deterministic Social Media Brand Audit • @${auditResult.handle}`,
      margin,
      pageHeight - 10
    );
    doc.text(`Page ${pageNum}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  };

  // =========================================================================
  // PAGE 1: EXECUTIVE SUMMARY & RADAR OVERVIEW
  // =========================================================================
  let y = 20;

  // Header Logo or Agency Name
  if (logoBase64) {
    try {
      const imgProps = doc.getImageProperties(logoBase64);
      const maxW = 38;
      const maxH = 16;
      const ratio = imgProps.width / imgProps.height;
      let w = maxW;
      let h = w / ratio;
      if (h > maxH) {
        h = maxH;
        w = h * ratio;
      }
      const format = logoBase64.includes('image/jpeg') ? 'JPEG' : 'PNG';
      doc.addImage(logoBase64, format, pageWidth - margin - w, 14, w, h);
    } catch (e) {
      console.warn('Could not embed white-label logo in PDF:', e);
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
  doc.text('PROPRIETARY DETERMINISTIC SOCIAL MEDIA AUDIT', margin, y);
  y += 8;

  doc.setFontSize(24);
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.text(`Instagram Audit: @${auditResult.handle}`, margin, y);
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
  doc.text(
    `Brand: ${auditResult.brandName}  |  Industry: ${auditResult.industry}  |  Date: ${new Date(auditResult.auditedAt).toLocaleDateString()}`,
    margin,
    y
  );
  y += 6;
  doc.text(`Prepared by: ${companyName || 'BrandGenius Strategy Team'}  |  Audit Mode: ${auditResult.dataCompleteness.mode}`, margin, y);
  y += 10;

  // Score Banner Card
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(margin, y, contentWidth, 42, 3, 3, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(32);
  doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
  doc.text(`${auditResult.overallScore}/100`, margin + 8, y + 20);

  doc.setFontSize(11);
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.text(`Grade: ${auditResult.grade} (${auditResult.riskLevel} Risk)`, margin + 8, y + 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
  doc.text(`Archetype: ${auditResult.brandArchetype}`, margin + 8, y + 37);

  // Executive Summary inside banner right column
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.text('Executive Summary', margin + 62, y + 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  const summaryLines = doc.splitTextToSize(auditResult.executiveSummary, contentWidth - 68);
  doc.text(summaryLines, margin + 62, y + 16);

  y += 50;

  // Key Metrics Snapshot Row
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.text('Key Performance & Readiness Snapshot', margin, y);
  y += 5;

  const snapMetrics = [
    { label: 'Engagement Rate', val: `${auditResult.keyMetricsSnapshot.engagementRatePct}%` },
    { label: 'Authority Ratio', val: `${auditResult.keyMetricsSnapshot.followerToFollowingRatio}:1` },
    { label: 'Posting Velocity', val: `${auditResult.keyMetricsSnapshot.postingCadencePerWeek}/wk` },
    { label: 'Handle SEO Score', val: `${auditResult.keyMetricsSnapshot.handleHygieneScore}/100` },
  ];

  const boxW = (contentWidth - 9) / 4;
  snapMetrics.forEach((m, idx) => {
    const bx = margin + idx * (boxW + 3);
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(bx, y, boxW, 18, 2, 2, 'FD');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
    doc.text(m.label, bx + 4, y + 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text(m.val, bx + 4, y + 14);
  });

  y += 26;

  // 6-Pillar Score Summary Table
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.text('6-Pillar Deterministic Scorecard', margin, y);
  y += 6;

  auditResult.categories.forEach((cat) => {
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, y, contentWidth, 10, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text(cat.categoryName, margin + 4, y + 6.5);

    doc.text(`Grade ${cat.grade}  •  ${cat.score}/100`, pageWidth - margin - 4, y + 6.5, { align: 'right' });
    y += 12;
  });

  y += 4;

  // Priority Action Roadmap
  if (auditResult.priorityActions.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text('Top Priority Consultant Actions', margin, y);
    y += 6;

    auditResult.priorityActions.slice(0, 4).forEach((item, i) => {
      const actionLines = doc.splitTextToSize(`${i + 1}. [${item.priority}] ${item.action}`, contentWidth - 6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
      doc.text(actionLines, margin + 2, y);
      y += actionLines.length * 4.5 + 2;
    });
  }

  // Custom Consultant Notes (if provided)
  if (customNotes && customNotes.trim()) {
    if (y > pageHeight - 45) {
      addPageFooter(1);
      doc.addPage();
      y = 20;
    } else {
      y += 4;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
    doc.text('Advisory Notes & Strategic Context', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    const notesLines = doc.splitTextToSize(customNotes.trim(), contentWidth);
    doc.text(notesLines, margin, y);
  }

  addPageFooter(1);

  // =========================================================================
  // PAGE 2+: DETAILED PILLAR EVALUATIONS & COMPETITOR BENCHMARK
  // =========================================================================
  let pageNumber = 2;
  doc.addPage();
  y = 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
  doc.text('Pillar-by-Pillar Diagnostic Breakdown', margin, y);
  y += 10;

  auditResult.categories.forEach((cat) => {
    if (y > pageHeight - 75) {
      addPageFooter(pageNumber);
      pageNumber++;
      doc.addPage();
      y = 20;
    }

    doc.setFillColor(238, 242, 255);
    doc.roundedRect(margin, y, contentWidth, 9, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
    doc.text(`${cat.categoryName} — ${cat.score}/100 (Grade ${cat.grade})`, margin + 3, y + 6);
    y += 13;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
    const whyLines = doc.splitTextToSize(`Why It Matters: ${cat.whyItMatters}`, contentWidth);
    doc.text(whyLines, margin, y);
    y += whyLines.length * 4 + 3;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text('Key Findings:', margin, y);
    y += 4.5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    cat.findings.forEach((finding) => {
      const fLines = doc.splitTextToSize(`• ${finding}`, contentWidth - 4);
      doc.text(fLines, margin + 2, y);
      y += fLines.length * 4 + 1;
    });

    if (cat.recommendations.length > 0) {
      y += 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
      doc.text('Actionable Recommendations:', margin, y);
      y += 4.5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
      cat.recommendations.forEach((rec) => {
        const rLines = doc.splitTextToSize(`→ ${rec}`, contentWidth - 4);
        doc.text(rLines, margin + 2, y);
        y += rLines.length * 4 + 1;
      });
    }

    y += 7;
  });

  if (auditResult.competitorComparison) {
    if (y > pageHeight - 70) {
      addPageFooter(pageNumber);
      pageNumber++;
      doc.addPage();
      y = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(darkSlate[0], darkSlate[1], darkSlate[2]);
    doc.text(
      `Competitor Benchmark: @${auditResult.handle} vs. @${auditResult.competitorComparison.competitorHandle}`,
      margin,
      y
    );
    y += 8;

    auditResult.competitorComparison.metrics.forEach((m) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`${m.metric}: ${m.brandValue} vs. ${m.competitorValue} (${m.delta})`, margin + 2, y);
      y += 5;
    });
  }

  addPageFooter(pageNumber);

  return doc;
};
